/**
 * Run `cb` once the MapLibre map's style is fully loaded — robustly.
 *
 * The naive pattern `if (map.isStyleLoaded()) cb(); else map.once('load', cb);`
 * has a footgun: `'load'` is a ONE-SHOT event that fires exactly once in a
 * map's lifetime. If the style is transiently not-loaded at call time (e.g.
 * mid basemap switch via setStyle, or any in-flight style update) but the map
 * has ALREADY fired 'load', that `once('load')` never fires again and `cb`
 * (which typically adds sources/layers) silently never runs.
 *
 * `'styledata'` re-fires on every style update, so we attach there and re-check
 * `isStyleLoaded()` each time — but styledata alone is not enough either:
 * `isStyleLoaded()` also stays false while TILES are pending, and tile
 * progress emits 'sourcedata', not 'styledata'. When the tiles finish there
 * is no styledata to observe the flip, so a bare styledata listener never
 * fires (bit the Reports map 2026-07-03 and the Live Map POI toggle
 * 2026-07-09 — see the src/CLAUDE.md style-ready gotcha). 'idle' always fires
 * once all loading and rendering settle, so it is the guarantee: whichever
 * signal arrives first runs `cb` exactly once and detaches both listeners.
 *
 * @param {object} map MapLibre map instance
 * @param {() => void} cb invoked once when the style is ready
 * @returns {() => void} cleanup that detaches the pending listeners (no-op if cb already ran)
 */
export function whenStyleReady(map, cb) {
  if (map.isStyleLoaded && map.isStyleLoaded()) {
    cb();
    return () => {};
  }
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    map.off('styledata', onData);
    map.off('idle', onIdle);
    cb();
  };
  const onData = () => {
    if (map.isStyleLoaded()) run();
  };
  const onIdle = () => run();
  map.on('styledata', onData);
  map.on('idle', onIdle);
  return () => {
    map.off('styledata', onData);
    map.off('idle', onIdle);
  };
}
